import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, ChartFrame, HexMark, Icon, LineChart, monogram, SimulatedTag, Stat, StatusDot, StatusPill } from '@hive/ui';
import { api, keeperUrl, type NetworkOverview, type NetworkVenueDetail } from '../api/client';
import { useLive, useNow } from '../api/hooks';
import { ScreenThumb } from '../components/ScreenThumb';
import { Shell } from '../components/Shell';
import { ago, healthLabel, hhmm, isSim, num } from '../format';

export function VenuePage() {
  const { venueId = '' } = useParams();
  const now = useNow(5000);
  const { data: network, live } = useLive<NetworkOverview>(async () => (await api.GET('/v1/network')).data, [], 20_000);
  const { data, error } = useLive<NetworkVenueDetail>(
    async () => (await api.GET('/v1/network/venues/{venueId}', { params: { path: { venueId } } })).data,
    [venueId],
  );

  if (error && !data) {
    return (
      <Shell network={network} live={live}>
        <div className="mc-page">
          <p className="hv-muted">This venue does not exist in your tenant.</p>
        </div>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell network={network} live={live}>
        <div className="mc-page hv-muted">Loading venue…</div>
      </Shell>
    );
  }

  const v = data.venue;
  const sim = v.source === 'simulated';
  const k = data.kpis;
  // Screens grouped by the venue's own areas; the drawn floor plan is gone (its geometry was made up).
  const byArea = new Map<string, typeof data.screens>();
  for (const s of data.screens) (byArea.get(s.area ?? '') ?? byArea.set(s.area ?? '', []).get(s.area ?? '')!).push(s);
  const areaGroups = [...byArea.entries()].sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));
  const window = { from: now - 3600_000, to: now };

  return (
    <Shell network={network} live={live}>
      <div className="mc-page">
        <header className="mc-pagehead">
          <div className="mc-pagehead__left">
            <Breadcrumbs
              items={[
                { label: 'Network', href: '/network' },
                { label: v.country },
                { label: v.city, href: `/network?venue=${v.id}` },
                {
                  label: (
                    <>
                      <HexMark label={monogram(v.name)} size={22} tone="deep" />
                      {v.name}
                      {sim && <SimulatedTag title="Simulated venue" />}
                    </>
                  ),
                },
              ]}
              renderLink={(href, label) => <Link to={href}>{label}</Link>}
            />
            <StatusPill status={v.hasOfflineScreen && v.health !== 'offline' ? 'offline' : v.health}>
              {v.hasOfflineScreen && v.health !== 'offline' ? 'Degraded · screen offline' : healthLabel[v.health]}
            </StatusPill>
            <span className="mc-pill-soft">
              {data.occupancy.label} · {data.occupancy.percent}% <SimulatedTag title="Occupancy is simulated" />
            </span>
          </div>
          <div className="mc-pagehead__actions">
            <button type="button" className="hv-btn hv-btn--secondary hv-btn--sm" disabled title="Not in the prototype">
              Push message to screens
            </button>
            <Link to={`/venues/${v.id}/setup`} className="hv-btn hv-btn--secondary hv-btn--sm">
              Set up venue
            </Link>
            {!sim && (
              <a href={`${keeperUrl}/?venue=${v.id}`} target="_blank" rel="noreferrer" className="hv-btn hv-btn--primary hv-btn--sm">
                Open in Keeper
              </a>
            )}
          </div>
        </header>

        <section aria-label="Venue at a glance" className="mc-kpis6">
          <Stat bordered label="Screens" value={v.screensOnline} unit={`of ${v.screensTotal} online`} />
          <Stat bordered label="Venue QoE" value={num(k.qoe)} unit={k.qoeTrend ?? ''} source={k.qoe.source} />
          <Stat bordered label="Avg bitrate" value={num(k.avgBitrateKbps, 1, 1 / 1000)} unit="Mbps" source={k.avgBitrateKbps.source} />
          <Stat bordered label="Rebuffer ratio" value={num(k.rebufferRatio, 1)} unit="%" source={k.rebufferRatio.source} />
          <Stat bordered label="Live latency" value={num(k.latencyMs, 1, 1 / 1000)} unit="s behind source" source={k.latencyMs.source} />
          <Stat bordered label="Nectar tonight" value={data.nectar.orders} unit={`orders · ${data.nectar.scans} scans`} source={data.nectar.source === 'simulated' ? 'simulated' : undefined} />
        </section>

        {data.sync && data.sync.screens.length > 1 && (
          <section aria-label="Screens together" className="hv-card mc-sync">
            <span className="mc-section-title">Screens together</span>
            <span className={`mc-sync__spread${data.sync.spreadMs != null && data.sync.spreadMs > 100 ? ' mc-warn' : ''}`}>
              {data.sync.spreadMs != null ? `${data.sync.spreadMs} ms apart${data.sync.channelName ? ` on ${data.sync.channelName}` : ''}` : 'Needs two screens on one stream to measure'}
            </span>
            <span className="mc-small hv-muted">
              {data.sync.targetSeconds ? `Target ${data.sync.targetSeconds} s behind live` : 'No venue target: each stream keeps its own offset'}
              {data.sync.audioScreenId ? ` · sound from ${data.sync.screens.find((x) => x.screenId === data.sync!.audioScreenId)?.name ?? 'one screen'}` : ' · every screen plays sound'}
            </span>
            <span className="mc-sync__list mc-small">
              {data.sync.screens.map((x) => (
                <span key={x.screenId}>
                  {x.name}: {x.latencyMs != null ? `${(x.latencyMs / 1000).toFixed(2)} s` : 'not playing'}
                  {x.muted ? ' · muted' : ''}
                </span>
              ))}
            </span>
          </section>
        )}

        <div>
          <section aria-label="Screens" className="mc-screens">
            {areaGroups.map(([area, list]) => (
              <div key={area || 'none'} className="mc-area">
                {(areaGroups.length > 1 || area) && (
                  <span className="mc-area__name">
                    {area || 'No area'} <span className="hv-muted">· {list.length} screen{list.length === 1 ? '' : 's'}</span>
                  </span>
                )}
            {list.map((s) => (
              <Link key={s.id} to={`/screen/${s.id}`} className={`hv-card mc-screenrow mc-screenrow--${s.status}`}>
                <ScreenThumb screen={s} />
                <div className="mc-screenrow__body">
                  <div className="mc-row-between">
                    <span className="mc-strong">
                      <StatusDot status={s.status === 'offline' ? 'offline' : s.status === 'unpaired' ? 'idle' : 'healthy'} /> {s.name}
                      {s.code ? ` · ${s.code}` : ''}
                    </span>
                    <span className={s.status === 'offline' ? 'mc-bad mc-small' : 'mc-good mc-small'}>
                      {s.status === 'offline'
                        ? `Offline · last heartbeat ${ago(s.lastHeartbeatAt, now)} ago`
                        : s.nowPlaying
                          ? `${s.nowPlaying.status === 'live' ? 'Live' : s.nowPlaying.kind === 'loop' ? 'Sponsor loop' : 'On'} · ${s.nowPlaying.competition ?? s.nowPlaying.title}`
                          : s.status === 'unpaired'
                            ? 'Not paired'
                            : 'Ambient screen'}
                    </span>
                  </div>
                  <span className="hv-muted mc-small">
                    {[s.nowPlaying?.title, s.sizeInches && `${s.sizeInches} in`, s.connection].filter(Boolean).join(' · ')}
                  </span>
                  <div className="mc-metrics">
                    <span>
                      <span className="mc-metrics__big">{num(s.qoe)}</span> QoE
                    </span>
                    <span>{num(s.bitrateKbps, 1, 1 / 1000)} Mbps</span>
                    <span>{num(s.rebufferRatio, 1)}% rebuffer</span>
                    <span>{num(s.latencyMs, 1, 1 / 1000)} s latency</span>
                    <span>{num(s.droppedFrames)} drops</span>
                    {isSim(s.qoe) && <SimulatedTag />}
                  </div>
                </div>
                <Icon name="chevronRight" size={18} />
              </Link>
            ))}
              </div>
            ))}
          </section>
        </div>

        <div className="mc-venue-grid mc-venue-grid--even">
          <section aria-label="Activity" className="hv-card mc-activity">
            <div className="mc-row-between">
              <span className="mc-section-title">Activity in venue</span>
              <span className="hv-muted mc-small">Keeper, screens and Nectar events</span>
            </div>
            <ol className="mc-feed">
              {data.activity.map((a, i) => (
                <li key={i}>
                  <span className="mc-feed__time">{hhmm(a.at)}</span>
                  <StatusDot status={a.kind === 'disconnected' ? 'offline' : a.kind === 'connected' ? 'healthy' : a.kind === 'nectar' || a.kind === 'promo' ? 'promo' : 'idle'} />
                  <span>
                    {a.message}
                    {a.source === 'simulated' && <SimulatedTag />}
                  </span>
                </li>
              ))}
              {data.activity.length === 0 && <li className="hv-muted">No activity yet</li>}
            </ol>
          </section>
          <ChartFrame title="Venue QoE · last 60 min" meta="Composite of rebuffer, bitrate, latency, drops" source={data.qoeSeries.source === 'simulated' ? 'simulated' : undefined}>
            <LineChart
              points={data.qoeSeries.points}
              from={window.from}
              to={window.to}
              min={60}
              max={100}
              threshold={{ value: 85, label: 'Degraded below 85' }}
              annotations={data.qoeSeries.annotations}
              ariaLabel={`Venue QoE over the last 60 minutes, currently ${num(k.qoe)}`}
            />
          </ChartFrame>
        </div>

        <section id="rights" aria-label="Licence and rights" className="hv-card mc-rights">
          <span className="mc-section-title">Licence and rights</span>
          {data.licence && (
            <span className="mc-small">
              Licence {data.licence.licenceNo} · {data.licence.tier}
              {data.licence.source === 'simulated' && <SimulatedTag />}
            </span>
          )}
          <div className="mc-list">
            {data.rights.map((r) => (
              <div key={r.id} className={`mc-list__row${r.status !== 'active' ? ' mc-list__row--warn' : ''}`}>
                <StatusDot status={r.status === 'active' ? 'healthy' : 'degraded'} />
                <span className="mc-strong mc-grow">{r.competition}</span>
                <span className="hv-muted mc-small">{[r.territory, r.scope].filter(Boolean).join(' · ')}</span>
                <span className={r.status === 'active' ? 'hv-muted mc-small' : 'mc-warn mc-small'}>{r.status === 'active' ? `${r.daysLeft} days left` : `Expires in ${r.daysLeft} days`}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Shell>
  );
}
